import { formatDate } from '@enter/core';
import Icon from '@mdi/react';
import { mdiAutoFix, mdiCheck, mdiClipboardListOutline } from '@mdi/js';
import React from 'react';
import { Variables } from '../../base';
import * as S from './statement-ledger-dialog.styles';
import {
    ApplyMode,
    buildFifoNote,
    formatLedgerMoney,
    StatementClaimOption,
} from './statement-apply-to-claims.utils';

type StatementApplyToClaimsProps = {
    applyMode: ApplyMode;
    onApplyModeChange?: (mode: ApplyMode) => void;
    openClaims: StatementClaimOption[];
    /** Claims offered in "Select claims" mode; defaults to openClaims. */
    selectableClaims?: StatementClaimOption[];
    selectedClaimIds: Record<string, boolean>;
    onToggleClaim: (claimId: string) => void;
    targetClaims: StatementClaimOption[];
    errorMessage?: string;
    /** Payment recording hides invoice balance; it stays $0 until patient collections is enabled. */
    hideBalance?: boolean;
    /** Payment recording has no Recommended mode; the claim list is the only way to apply. */
    selectionOnly?: boolean;
};

export const StatementApplyToClaims: React.FunctionComponent<StatementApplyToClaimsProps> = ({
    applyMode,
    onApplyModeChange,
    openClaims,
    selectableClaims,
    selectedClaimIds,
    onToggleClaim,
    targetClaims,
    errorMessage,
    hideBalance = false,
    selectionOnly = false,
}) => {
    const claimsToList = selectableClaims ?? openClaims;
    const selectedCount = targetClaims.length;
    const selectedBalanceTotal = targetClaims.reduce((sum, claim) => sum + claim.balance, 0);
    const fifoNote = buildFifoNote(openClaims);
    const showClaimList = selectionOnly || applyMode === 'manual';

    return (
        <S.FormSection>
            <S.Label>Apply to</S.Label>
            {!selectionOnly && (
                <S.ApplyModeRow>
                    <S.ApplyModeButton
                        type="button"
                        variant="unstyled"
                        $active={applyMode === 'auto'}
                        onClick={() => onApplyModeChange?.('auto')}>
                        <Icon path={mdiAutoFix} size={0.7} />
                        Recommended
                    </S.ApplyModeButton>
                    <S.ApplyModeButton
                        type="button"
                        variant="unstyled"
                        $active={applyMode === 'manual'}
                        onClick={() => onApplyModeChange?.('manual')}>
                        <Icon path={mdiClipboardListOutline} size={0.7} />
                        Select claims
                    </S.ApplyModeButton>
                </S.ApplyModeRow>
            )}

            {!showClaimList ? (
                <S.FifoNote>
                    <Icon path={mdiAutoFix} size={0.7} color={Variables.Colors.b300} />
                    <span>{fifoNote}</span>
                </S.FifoNote>
            ) : (
                <>
                    <S.ClaimList>
                        {claimsToList.map(claim => {
                            const checked = !!selectedClaimIds[claim.id];
                            return (
                                <S.ClaimRow
                                    key={claim.id}
                                    type="button"
                                    variant="unstyled"
                                    onClick={() => onToggleClaim(claim.id)}>
                                    <S.CheckBox checked={checked}>
                                        {checked && (
                                            <Icon
                                                path={mdiCheck}
                                                size={0.55}
                                                color={Variables.Swatches.White}
                                            />
                                        )}
                                    </S.CheckBox>
                                    <S.ClaimMeta>
                                        <S.ClaimTitle>Claim #{claim.externalId || '—'}</S.ClaimTitle>
                                        <S.ClaimSubtitle>
                                            {claim.fromDos
                                                ? formatDate(claim.fromDos, 'MMM dd, yyyy')
                                                : '—'}
                                            {!hideBalance && (
                                                <>
                                                    {' · '}
                                                    {claim.pendingAdjudication
                                                        ? 'Pending'
                                                        : claim.balance > 0
                                                          ? 'Open balance'
                                                          : 'No open balance'}
                                                </>
                                            )}
                                            {hideBalance && claim.pendingAdjudication && ' · Pending'}
                                        </S.ClaimSubtitle>
                                    </S.ClaimMeta>
                                    {!hideBalance && (
                                        <S.ClaimBalance>{formatLedgerMoney(claim.balance)}</S.ClaimBalance>
                                    )}
                                </S.ClaimRow>
                            );
                        })}
                        {!claimsToList.length && (
                            <S.ClaimRow type="button" variant="unstyled" disabled>
                                <S.ClaimMeta>
                                    <S.ClaimSubtitle>No claims on this statement</S.ClaimSubtitle>
                                </S.ClaimMeta>
                            </S.ClaimRow>
                        )}
                    </S.ClaimList>
                    {!selectionOnly && (
                        <S.ApplySummary>
                            <span>
                                {selectedCount} of {claimsToList.length} claims selected
                            </span>
                            {!hideBalance && (
                                <S.ApplySummaryTotal>
                                    {formatLedgerMoney(selectedBalanceTotal)} selected
                                </S.ApplySummaryTotal>
                            )}
                        </S.ApplySummary>
                    )}
                    {selectionOnly && !hideBalance && (
                        <S.ApplySummary>
                            <span>
                                {selectedCount === 1 ? '1 claim selected' : 'No claim selected'}
                            </span>
                            <S.ApplySummaryTotal>
                                {formatLedgerMoney(selectedBalanceTotal)} selected
                            </S.ApplySummaryTotal>
                        </S.ApplySummary>
                    )}
                </>
            )}
            {errorMessage && <S.ErrorText>{errorMessage}</S.ErrorText>}
        </S.FormSection>
    );
};
